import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { Trans } from '@lingui/react/macro'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { BrandLoadingScreen } from '~/components/brand/brand-loader'
import { ProposalPublicView } from '~/components/funnel/proposal-public-view'
import { PublicMessage, PublicShell } from '~/components/funnel/public-shell'
import { capture, setWorkspaceGroup } from '~/lib/posthog'
import { api } from '~convex/_generated/api'

export const Route = createFileRoute('/p/$token')({
  component: RouteComponent,
  // Token-bearing client links must never become search results or leak their
  // path through outbound Referer headers. robots.txt is only advisory, so
  // keep the document-level policies even for crawlers that ignore it.
  head: () => ({
    meta: [
      { content: 'noindex, nofollow', name: 'robots' },
      { content: 'no-referrer', name: 'referrer' },
    ],
  }),
  // Public unauthenticated surface — runs entirely against the Convex client.
  ssr: false,
})

function RouteComponent() {
  const { token } = Route.useParams()
  const { data: payload, isPending } = useQuery(convexQuery(api.documents.getByToken, { token }))
  const { mutate: markOpened } = useMutation({
    mutationFn: useConvexMutation(api.documents.markOpened),
  })
  const marked = useRef(false)
  const tracked = useRef(false)

  useEffect(() => {
    if (payload?.status !== 'sent' || marked.current) {
      return
    }
    marked.current = true
    markOpened({ token })
  }, [payload, markOpened, token])

  // Anonymous — the prospect reading this is not our user and is never
  // identified. `status` covers the dead-link cases too: a client clicking a
  // revoked or expired proposal is a thing the workspace wants to know.
  useEffect(() => {
    if (payload === undefined || tracked.current) {
      return
    }
    tracked.current = true
    // Grouped before the capture so this event carries the workspace, and so
    // does every later one on this page — the accept/decline in
    // `proposal-public-view.tsx` included.
    if ('workspaceId' in payload && payload.workspaceId) {
      setWorkspaceGroup(payload.workspaceId)
    }
    capture('client_link_opened', { kind: 'proposal', status: payload.status })
  }, [payload])

  if (isPending || payload === undefined) {
    return <BrandLoadingScreen />
  }

  if (payload.status === 'not_found') {
    return (
      <PublicShell>
        <PublicMessage
          body={<Trans>This proposal link is not valid.</Trans>}
          title={<Trans>Link not found</Trans>}
        />
      </PublicShell>
    )
  }

  if (payload.status === 'revoked') {
    return (
      <PublicShell>
        <PublicMessage
          body={<Trans>This proposal is no longer available.</Trans>}
          title={<Trans>Link expired</Trans>}
        />
      </PublicShell>
    )
  }

  if (payload.status === 'expired') {
    return (
      <PublicShell>
        <PublicMessage
          body={<Trans>This proposal has expired and can no longer be accepted.</Trans>}
          title={<Trans>Proposal expired</Trans>}
        />
      </PublicShell>
    )
  }

  // No `PublicShell` here: its 448px column is what flattened six designs into
  // one narrow card. The view reads the design's canvas and draws its own page.
  return <ProposalPublicView initialStatus={payload.status} payload={payload} token={token} />
}
